import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useRole() {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [points, setPoints] = useState(0);
  const [allocatedRemaining, setAllocatedRemaining] = useState(0);
  const [allocations, setAllocations] = useState<Array<{ service_key: string; allocated_points: number; used_points: number }>>([]);
  const [countryCode, setCountryCode] = useState<string>("OTHERS");
  const [language, setLanguage] = useState<string>("ar");
  const [plan, setPlan] = useState<string>("standard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const computeAllocRemaining = (rows: any[] | null | undefined) => {
    if (!rows) return 0;
    return rows.reduce((sum, r) => sum + Math.max(Number(r.allocated_points || 0) - Number(r.used_points || 0), 0), 0);
  };

  const fetchData = useCallback(async (attempt = 0): Promise<void> => {
    if (authLoading) { setLoading(true); return; }
    if (!user) { setRoles([]); setPoints(0); setAllocatedRemaining(0); setAllocations([]); setLoading(false); setError(null); return; }
    try {
      const [rolesRes, profileRes, allocRes] = await Promise.all([
        (supabase as any).from("user_roles").select("role").eq("user_id", user.id),
        (supabase as any).from("profiles").select("points, country_code, language, plan").eq("id", user.id).single(),
        (supabase as any).from("service_allocations").select("service_key, allocated_points, used_points").eq("user_id", user.id),
      ]);
      if (rolesRes.error) throw rolesRes.error;
      setRoles(rolesRes.data?.map((r: any) => r.role) || []);
      setPoints(Number((profileRes.data as any)?.points || 0));
      setAllocatedRemaining(computeAllocRemaining(allocRes.data));
      setAllocations((allocRes.data || []).map((r: any) => ({
        service_key: r.service_key,
        allocated_points: Number(r.allocated_points || 0),
        used_points: Number(r.used_points || 0),
      })));
      setCountryCode((profileRes.data as any)?.country_code || "OTHERS");
      setLanguage((profileRes.data as any)?.language || "ar");
      setPlan((profileRes.data as any)?.plan || "standard");
      setError(null);
      setLoading(false);
    } catch (e: any) {
      // Transient network failure — retry a couple times before giving up so we
      // don't falsely drop admin privileges and redirect away from /admin.
      if (attempt < 2) {
        setTimeout(() => { fetchData(attempt + 1); }, 600 * (attempt + 1));
        return;
      }
      setError(e?.message || "Failed to load role");
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshPoints = useCallback(async () => {
    if (!user) return;
    const [profRes, allocRes] = await Promise.all([
      (supabase as any).from("profiles").select("points").eq("id", user.id).single(),
      (supabase as any).from("service_allocations").select("service_key, allocated_points, used_points").eq("user_id", user.id),
    ]);
    setPoints(Number((profRes.data as any)?.points || 0));
    setAllocatedRemaining(computeAllocRemaining(allocRes.data));
    setAllocations((allocRes.data || []).map((r: any) => ({
      service_key: r.service_key,
      allocated_points: Number(r.allocated_points || 0),
      used_points: Number(r.used_points || 0),
    })));
  }, [user]);

  const totalPoints = points + allocatedRemaining;

  return {
    roles,
    points, // general-pool only (legacy)
    totalPoints, // general + remaining allocated
    allocatedRemaining,
    allocations,
    credit: totalPoints, // backwards-compat alias
    countryCode,
    language,
    plan,
    isEnterprise: plan === "enterprise",
    loading,
    error,
    isAdmin: roles.includes("admin") || roles.includes("superadmin"),
    isSuperAdmin: roles.includes("superadmin"),
    refreshPoints,
    refreshCredit: refreshPoints, // backwards-compat alias
    refetch: fetchData,
  };
}
